import React, { useState, useLayoutEffect, useRef } from 'react';
import './ServicioTecnico.css';
import ContactModal from './ContactModal';
import Footer from './Footer';
import NavBar from './NavBar';
import Raiz from './raiz';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

function ServicioTecnico() {
  const root = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useLayoutEffect(() => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) return;

    const ctx = gsap.context(() => {
      const defaultScrollConfig = {
        start: 'top 85%',
        toggleActions: 'play none none none',
      };

      // Hero animation
      const heroTl = gsap.timeline({ defaults: { ease: 'power3.out' } });
      heroTl
        .fromTo(
          '.hero-branch-image-servicio',
          { autoAlpha: 0, scale: 1.03 },
          { autoAlpha: 1, scale: 1, duration: 1.1, ease: 'power2.out' }
        )
        .fromTo(
          '.hero-content-servicio > *',
          { autoAlpha: 0, y: 24 },
          { autoAlpha: 1, y: 0, duration: 0.9, stagger: 0.12 },
          '-=0.6'
        );

      // Diagonal separator
      gsap.fromTo(
        '.diagonal-separator-servicio',
        { scaleX: 0, transformOrigin: 'left center' },
        {
          scaleX: 1,
          duration: 1.2,
          ease: 'expo.inOut',
          scrollTrigger: {
            trigger: '.options-section',
            ...defaultScrollConfig,
          },
        }
      );

      // Service option cards
      gsap.fromTo(
        '.service-option-card',
        { autoAlpha: 0, y: 60, scale: 0.9 },
        {
          autoAlpha: 1,
          y: 0,
          scale: 1,
          duration: 0.8,
          stagger: 0.2,
          ease: 'back.out(1.2)',
          scrollTrigger: {
            trigger: '.service-options-grid',
            ...defaultScrollConfig,
          },
        }
      );

      // Tech features
      gsap.fromTo(
        '.tech-feature',
        { autoAlpha: 0, x: -30 },
        {
          autoAlpha: 1,
          x: 0,
          duration: 0.7,
          stagger: 0.1,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: '.tech-features',
            ...defaultScrollConfig,
          },
        }
      );
    }, root);

    return () => ctx.revert();
  }, []);

  return (
    <div className="ServicioTecnico" ref={root}>
      {/* Header */}
      <NavBar />

      {/* Hero Section */}
      <section className="servicio-hero">
        <img src="/assets/fondost.png" alt="Branch Service" className="hero-branch-image-servicio" />
        <div className="hero-content-servicio">
          <h1 className="hero-title-servicio">Servicio Técnico Especializado</h1>
          <p className="hero-subtitle-servicio">
            Equipamiento médico y de laboratorio
          </p>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="intro-section">
        <div className="container">
          <div className="intro-header">
            <h2>Mantenimiento y Reparación de Equipamiento de Laboratorio</h2>
            <div className="header-line"></div>
          </div>
          <p className="intro-description">
            En Branch Development brindamos diagnóstico, reparación y mantenimiento preventivo/correctivo
            para autoanalizadores, contadores hematológicos, espectrofotómetros, equipos de gases en sangre
            y una amplia variedad de instrumental de laboratorio.
          </p>
          <p className="intro-description">
            Trabajamos con un enfoque profesional orientado a la continuidad operativa y la confiabilidad
            de resultados: evaluación técnica, puesta a punto, ajustes, verificación de funcionamiento y
            registro con informe técnico para garantizar trazabilidad.
          </p>
        </div>
      </section>

      {/* Services Options Section */}
      <section className="options-section">
        <div className="diagonal-separator-servicio"></div>
        <div className="container">
          <div className="options-header">
            <h2>Elegí Cómo Gestionar el Servicio</h2>
            <div className="header-line"></div>
          </div>
          <p className="options-intro">
            Podés elegir cómo querés gestionar el servicio según tus necesidades:
          </p>

          <div className="service-options-grid">
            {/* Branch Development Option */}
            <div className="service-option-card">
              <div className="option-logo branch-logo">
                <img src="/assets/BRANCHDEVELOPMENTLOGO.png" alt="Branch Development" />
              </div>
              <div className="option-header">
                <h3>Branch Development</h3>
                <div className="option-line"></div>
              </div>
              <p className="option-description">
                Atención coordinada, seguimiento integral del caso y soporte directo en laboratorio o remoto.
              </p>
              <ul className="option-features">
                <li>Atención coordinada directa</li>
                <li>Seguimiento integral del caso</li>
                <li>Soporte en sitio o remoto</li>
                <li>Tiempos de respuesta priorizados</li>
              </ul>
            </div>

            {/* Urani Biomedica Option */}
            <div
              className="service-option-card urani-card"
              onClick={() => window.open('https://www.uranibiomedica.com')}
            >
              <div className="option-logo urani-logo">
                <img src="/assets/UraniLOGO.PNG" alt="Urani Biomédica" />
              </div>
              <div className="option-header">
                <h3>Urani Biomédica</h3>
                <div className="option-line"></div>
              </div>
              <p className="option-description">
                Opción complementaria con acompañamiento técnico y coordinación para intervenciones,
                validaciones y puesta en marcha.
              </p>
              <ul className="option-features">
                <li>Acompañamiento técnico especializado</li>
                <li>Coordinación de intervenciones</li>
                <li>Validaciones y certificaciones</li>
                <li>Puesta en marcha de equipos</li>
              </ul>
            </div>
          </div>

          <div className="service-benefits">
            <h3>Nuestro Compromiso</h3>
            <div className="benefits-grid">
              <div className="benefit-item">
                <h4>Atención en Sitio</h4>
                <p>Servicio técnico directo en tu laboratorio</p>
              </div>
              <div className="benefit-item">
                <h4>Soporte Remoto</h4>
                <p>Asistencia técnica a distancia</p>
              </div>
              <div className="benefit-item">
                <h4>Tiempos de Respuesta</h4>
                <p>Prioridad en la atención de casos</p>
              </div>
              <div className="benefit-item">
                <h4>Soluciones Duraderas</h4>
                <p>Reparaciones con garantía y seguimiento</p>
              </div>
            </div>
          </div>

          <div className="cta-container">
            <Raiz
              text="Solicitar Servicio"
              onClick={() => setIsModalOpen(true)}
            />
          </div>
        </div>
      </section>

      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />

      <Footer />
    </div>
  );
}

export default ServicioTecnico;
